import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { PulseDot, Spinner, Toast } from '../../components/design-system';
import {
  TripDetailPanel,
  type TripDetailData,
  type TripStage,
  type BuildSubState,
} from '../../components/host/TripDetailPanel';
import { MobileTripBoard, type BoardStage } from '../../components/host/MobileTripBoard';
import { defaultStage } from '../../lib/mobileBoard';
import { formatMoney, INVOICE_STATE_LABEL, invoiceState, type InvoiceState } from '../../lib/hostInvoice';
import { HostInvoiceModal } from '../../components/host/HostInvoiceModal';
import { cn } from '../../lib/utils';
import { hostProfileStrength, strengthHeadline } from '../../lib/hostProfileStrength';
import type { HostProfileStrength } from '../../lib/hostProfileStrength';
import type { HandbookContent, HostProfile } from '../../types';

/**
 * HostDashboard — "Your trips" pipeline board.
 * Design source: Host Trips Kanban.dc.html
 *
 * Three views (kanban / list / calendar), persisted per device.
 * Cards/rows/bars open the stage-aware TripDetailPanel slide-over.
 *
 * Data loading is identical to the previous Dashboard.tsx (matches +
 * itineraries + profile nudge). New logic added on top:
 *   • stage derivation (inquiry/building/confirmed/active/past)
 *   • accept/pass mutations for `proposed` inquiries
 *   • view-mode persistence (localStorage `sk_trips_view`)
 */

// ── Types ─────────────────────────────────────────────────────────────────────

interface TripMatch {
  id: string;
  traveler_id: string;
  status: string;
  created_at: string;
  traveler_name: string | null;
  itinerary_status: string | null;
  trip_name: string | null;
  destination: string | null;
  start_date: string | null;
  end_date: string | null;
  events_planned: number;
  progress: number;
  /** Alma's read on the inquiry (taste_portraits.host_summary). */
  alma_summary: string | null;
  /** Interest tags pulled from the taste portrait. */
  interests: string[];
  /** The traveler's ask, pulled from the taste portrait. */
  ask: string | null;
  /** Trip pace ('slow' | 'medium' | 'fast'), from the taste portrait. */
  pace: string | null;
  /** Who's traveling ('solo' | 'couple' | 'group'), from the taste portrait. */
  party: string | null;
  /** Things to avoid, from the taste portrait. */
  dealbreakers: string[];
  /** The money side of this trip, for the "Ready to invoice" stage. */
  payment: TripPayment | null;
}

interface TripPayment {
  id: string;
  /** What Sekretly pays the host for this trip (the host fee, VAT included if the host charges it). */
  fee_cents: number;
  currency: string;
  state: InvoiceState;
  /** The invoice this trip is on right now, if any. */
  invoice: { invoice_number: string; status: string } | null;
  /** Why the last invoice for this trip was sent back, shown while it is due again. */
  rejectedReason: string | null;
}

/** "Lisbon", "Lisbon & Porto" — never just the first. */
function formatPlaces(places: string[]): string {
  if (places.length <= 1) return places[0] ?? '';
  return `${places.slice(0, -1).join(', ')} & ${places[places.length - 1]}`;
}

/** Add n days to a YYYY-MM-DD date, returning YYYY-MM-DD. */
function addDaysISO(date: string, n: number): string {
  const d = new Date(date + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Defensive readers for the free-form portrait JSON. */
function extractInterests(pj: Record<string, unknown> | null): string[] {
  if (!pj) return [];
  for (const key of ['interests', 'tags', 'themes']) {
    const v = pj[key];
    if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string').slice(0, 6);
  }
  return [];
}

function extractAsk(pj: Record<string, unknown> | null): string | null {
  if (!pj) return null;
  // 'raw_summary' is the field the current portrait-extraction schema
  // actually writes (Alma's own reflection back to the traveler); the
  // others are kept for older/differently-shaped rows.
  for (const key of ['raw_summary', 'ask', 'summary', 'wishes', 'trip_summary']) {
    const v = pj[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

const PACE_LABEL: Record<string, string> = { slow: 'Slow & unhurried', medium: 'Balanced pace', fast: 'Fast-paced' };
const PARTY_LABEL: Record<string, string> = { solo: 'Traveling solo', couple: 'Couple', group: 'Group' };

function extractPace(pj: Record<string, unknown> | null): string | null {
  const v = pj?.pace;
  return typeof v === 'string' && v in PACE_LABEL ? v : null;
}

function extractParty(pj: Record<string, unknown> | null): string | null {
  const v = pj?.social;
  return typeof v === 'string' && v in PARTY_LABEL ? v : null;
}

function extractDealbreakers(pj: Record<string, unknown> | null): string[] {
  if (!pj) return [];
  const v = pj.dealbreakers;
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string').slice(0, 6) : [];
}

// ── Stage derivation (single source of truth) ────────────────────────────────

function deriveStage(t: TripMatch, now: Date): { stage: TripStage; buildState?: BuildSubState } {
  const start = t.start_date ? new Date(t.start_date) : null;
  const end = t.end_date ? new Date(t.end_date) : null;

  // A 'pending' match = traveler paid and is awaiting this host's confirmation.
  if (t.status === 'pending') return { stage: 'inquiry' };
  // A finished trip the host can be (or is being) paid for stays in "Ready to
  // invoice" until the money has arrived.
  if (t.status === 'completed' && t.payment && ['due', 'sent', 'approved'].includes(t.payment.state)) return { stage: 'invoice' };
  if (t.status === 'completed' || (end && end < now)) return { stage: 'past' };
  if (start && end && start <= now && end >= now) return { stage: 'active' };
  if (t.itinerary_status === 'approved') return { stage: 'confirmed', buildState: 'approved' };
  if (t.itinerary_status === 'submitted') return { stage: 'building', buildState: 'waiting' };
  if (t.itinerary_status === 'draft') return { stage: 'building', buildState: 'draft' };
  return { stage: 'building', buildState: 'unstarted' };
}

function toTripDetail(t: TripMatch, now: Date): TripDetailData {
  const { stage, buildState } = deriveStage(t, now);
  const days = t.start_date && t.end_date
    ? Math.round((new Date(t.end_date).getTime() - new Date(t.start_date).getTime()) / 86400000) + 1
    : 1;
  return {
    matchId: t.id,
    travelerName: t.traveler_name ?? 'Traveler',
    initials: initialsOf(t.traveler_name),
    avatarColor: avatarColorOf(t.traveler_id),
    tripName: t.trip_name ?? 'New trip',
    city: t.destination ?? '',
    dateRange: dateRange(t) ?? '',
    days,
    eventsPlanned: t.events_planned || undefined,
    stage,
    buildState,
    progress: t.progress,
    submittedLabel: stage === 'inquiry' ? relativeTime(t.created_at) : undefined,
    // What Alma learned matters for the whole trip, not just the accept/pass
    // decision — the host needs it while building the handbook too, so this
    // is no longer hidden once a trip moves past the inquiry stage.
    interests: t.interests.length ? t.interests : undefined,
    ask: t.ask ?? undefined,
    almaBriefing: t.alma_summary ? { headline: t.alma_summary } : undefined,
    pace: t.pace ? PACE_LABEL[t.pace] : undefined,
    party: t.party ? PARTY_LABEL[t.party] : undefined,
    dealbreakers: t.dealbreakers.length ? t.dealbreakers : undefined,
    dayOfLabel: stage === 'active' && t.start_date
      ? `Day ${Math.min(Math.floor((now.getTime() - new Date(t.start_date).getTime()) / 86400000) + 1, days)} of ${days}`
      : undefined,
    completedLabel: (stage === 'past' || stage === 'invoice') && t.end_date ? completedLabel(t.end_date, now) : undefined,
    invoiceLabel: stage === 'invoice' && t.payment
      ? `${INVOICE_STATE_LABEL[t.payment.state]} · ${formatMoney(t.payment.fee_cents, t.payment.currency)}`
      : undefined,
  };
}

// ── Small helpers ─────────────────────────────────────────────────────────────

function initialsOf(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
}

/** Stable image-gradient-palette color from the traveler id. */
const AVATAR_COLORS = ['#3d4d70', '#574a6b', '#8a5b30', '#43603c', '#7a2f3f', '#6b4a2f', '#3a3f47', '#5a4a6b'];
function avatarColorOf(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function dateRange(t: TripMatch): string | null {
  if (!t.start_date || !t.end_date) return null;
  const s = new Date(t.start_date);
  const e = new Date(t.end_date);
  const sTxt = s.toLocaleDateString('en', { month: 'short', day: 'numeric' });
  const eTxt = e.toLocaleDateString('en', s.getMonth() === e.getMonth() ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
  return `${sTxt}–${eTxt}`;
}

function relativeTime(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const d = Math.floor(hrs / 24);
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

function completedLabel(endDate: string, now: Date): string {
  const d = Math.floor((now.getTime() - new Date(endDate).getTime()) / 86400000);
  if (d <= 1) return 'Completed yesterday';
  return `Completed ${d} days ago`;
}

function daysUntil(dateStr: string | null, now: Date): number | null {
  if (!dateStr) return null;
  return Math.round((new Date(dateStr).getTime() - now.getTime()) / 86400000);
}

function isHandbook(c: unknown): c is HandbookContent {
  return !!c && typeof c === 'object' && 'days' in (c as object);
}

/** Handbook completion: share of days that have at least one place. */
function handbookProgress(hb: HandbookContent | null): { progress: number; events: number } {
  if (!hb?.days?.length) return { progress: 0, events: 0 };
  const withPlaces = hb.days.filter((d) => (d.places?.length ?? 0) > 0).length;
  const events = hb.days.reduce((n, d) => n + (d.places?.length ?? 0), 0);
  return { progress: Math.round((withPlaces / hb.days.length) * 100), events };
}

// ── Stage visual language (columns, chips, calendar) ─────────────────────────

const STAGE_META: Record<TripStage, {
  label: string; icon: string;
  text: string; chip: string; bar: string; dot: string;
}> = {
  inquiry: {
    label: 'New inquiries', icon: 'ti-inbox',
    text: 'text-app-muted',
    chip: 'bg-[rgb(var(--text-dim-rgb)/0.10)] text-app-muted border-[rgb(var(--text-dim-rgb)/0.20)]',
    bar: 'bg-[rgb(var(--text-dim-rgb)/0.14)] border-[rgb(var(--text-dim-rgb)/0.33)]',
    dot: 'bg-app-muted',
  },
  building: {
    label: 'Building', icon: 'ti-pencil',
    text: 'text-violet',
    chip: 'bg-violet/10 text-violet border-violet/20',
    bar: 'bg-violet/15 border-violet/30',
    dot: 'bg-violet',
  },
  confirmed: {
    label: 'Confirmed', icon: 'ti-check',
    text: 'text-accent',
    chip: 'bg-gold/10 text-accent border-gold/20',
    bar: 'bg-gold/15 border-gold/30',
    dot: 'bg-gold',
  },
  active: {
    label: 'Active now', icon: 'ti-map-pin',
    text: 'text-success',
    chip: 'bg-success/10 text-success border-success/20',
    bar: 'bg-success/15 border-success/30',
    dot: 'bg-success',
  },
  invoice: {
    label: 'Ready to invoice', icon: 'ti-file-invoice',
    text: 'text-gold',
    chip: 'bg-gold/10 text-gold border-gold/25',
    bar: 'bg-gold/15 border-gold/30',
    dot: 'bg-gold',
  },
  past: {
    label: 'Completed', icon: 'ti-clock-check',
    text: 'text-app-muted',
    chip: 'bg-app-card text-app-muted border-app-border',
    bar: 'bg-[rgb(var(--text-dim-rgb)/0.08)] border-[rgb(var(--text-dim-rgb)/0.20)]',
    dot: 'bg-app-muted',
  },
};

const BUILD_LABEL: Record<BuildSubState, string> = {
  unstarted: 'Not started',
  draft: 'Draft',
  waiting: 'Waiting approval',
  approved: 'Approved',
};

// ── Shared bits ───────────────────────────────────────────────────────────────

function TravelerAvatar({ trip, size = 'md' }: { trip: TripMatch; size?: 'sm' | 'md' }) {
  return (
    <div
      className={cn('rounded-full flex items-center justify-center shrink-0', size === 'sm' ? 'w-7 h-7' : 'w-[30px] h-[30px]')}
      style={{ background: avatarColorOf(trip.traveler_id) }}
    >
      <span className="font-serif text-cream leading-none" style={{ fontSize: size === 'sm' ? 10 : 10.5 }}>
        {initialsOf(trip.traveler_name)}
      </span>
    </div>
  );
}

function StageChip({ stage, label }: { stage: TripStage; label?: string }) {
  const m = STAGE_META[stage];
  return (
    <span className={cn('inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-[3px] text-caption font-medium whitespace-nowrap', m.chip)}>
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', m.dot)} />
      {label ?? m.label}
    </span>
  );
}


// ── Kanban card ───────────────────────────────────────────────────────────────

interface CardProps {
  trip: TripMatch;
  stage: TripStage;
  buildState?: BuildSubState;
  now: Date;
  onOpen: () => void;
  onAccept?: () => void;
  onPass?: () => void;
  /** "Ready to invoice" cards: whether the trip is ticked, and how to tick it (only while it is due). */
  invoice?: { selected: boolean; onToggle?: () => void };
}

/** The money line of a "Ready to invoice" card: tick box (when due), state and amount. */
function InvoiceFooter({ trip, selected, onToggle }: { trip: TripMatch; selected: boolean; onToggle?: () => void }) {
  const p = trip.payment;
  if (!p) return null;
  const tone = p.state === 'due' ? 'text-gold' : p.state === 'approved' ? 'text-[color:var(--success-fg)]' : 'text-app-muted';
  return (
    <div className="mt-3 pt-2.5 border-t border-app-border">
      <div className="flex items-center gap-2">
        {p.state === 'due' && onToggle && (
          <label className="flex items-center justify-center cursor-pointer -m-3.5 p-3.5" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggle}
              className="h-4 w-4 accent-gold"
              aria-label={`Include ${trip.trip_name ?? 'this trip'} in your invoice`}
            />
          </label>
        )}
        <span className={cn('text-caption font-medium', tone)}>
          {INVOICE_STATE_LABEL[p.state]}{p.invoice ? ` · ${p.invoice.invoice_number}` : ''}
        </span>
        <span className="ml-auto text-footnote font-medium text-app-fg">{formatMoney(p.fee_cents, p.currency)}</span>
      </div>
      {p.state === 'due' && p.rejectedReason && (
        <p className="mt-1.5 text-caption text-crimson">Your last invoice was sent back: {p.rejectedReason}</p>
      )}
      {p.state === 'approved' && <p className="mt-1.5 text-caption text-app-muted">Paid in the next payment run.</p>}
    </div>
  );
}

function KanbanCard({ trip, stage, buildState, now, onOpen, onAccept, onPass, invoice }: CardProps) {
  const days = trip.start_date && trip.end_date
    ? Math.round((new Date(trip.end_date).getTime() - new Date(trip.start_date).getTime()) / 86400000) + 1
    : null;
  const until = daysUntil(trip.start_date, now);
  const isDraft = stage === 'building' && buildState === 'draft';
  const alertUrgent = isDraft && until != null && until <= 10;
  const alertWarn = isDraft && until != null && until > 10 && until <= 21;

  return (
    <div
      onClick={onOpen}
      className="group bg-app-card border border-app-border rounded-[10px] px-3.5 py-3 cursor-pointer shrink-0 transition-all duration-200 hover:-translate-y-0.5 hover:border-gold/30 hover:shadow-[var(--shadow-hover)]"
    >
      <div className="flex items-center gap-2 mb-2">
        <TravelerAvatar trip={trip} />
        <span className="text-footnote font-medium text-app-fg flex-1 min-w-0 truncate">{trip.traveler_name ?? 'Traveler'}</span>
        {days != null && (
          <span className="inline-flex items-center gap-1 shrink-0 text-caption2 font-medium text-app-muted border border-app-border rounded-pill px-2 py-px whitespace-nowrap bg-[rgb(var(--text-dim-rgb)/0.06)]">
            <i className="ti ti-route text-[length:calc(9.5px*var(--text-scale))] opacity-85" />{days} days
          </span>
        )}
      </div>

      <div className="font-serif text-subhead text-app-fg leading-[1.3] mb-1.5 [text-wrap:pretty]">{trip.trip_name ?? 'New trip'}</div>

      <div className="flex items-center gap-1.5 text-app-muted text-caption truncate">
        <i className="ti ti-map-pin text-[length:calc(10px*var(--text-scale))] shrink-0" />
        {trip.destination && <span className="shrink-0">{trip.destination}</span>}
        {trip.destination && dateRange(trip) && <span className="shrink-0">·</span>}
        {dateRange(trip) && <span className="shrink-0">{dateRange(trip)}</span>}
      </div>

      {/* Building sub-states */}
      {stage === 'building' && buildState === 'unstarted' && (
        <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-app-border text-caption2 text-app-muted">
          <i className="ti ti-file-text text-[length:calc(10px*var(--text-scale))]" />Handbook not started
        </div>
      )}
      {stage === 'building' && buildState === 'draft' && (
        <div className="mt-2.5">
          <div className="flex justify-between items-center mb-1">
            <span className="text-caption2 text-app-muted">Handbook draft</span>
            <span className="text-caption2 text-app-fg-2">{trip.progress}%</span>
          </div>
          <div className="h-[3px] bg-app-border rounded-md overflow-hidden">
            <div className="h-full bg-violet rounded-md transition-[width]" style={{ width: `${trip.progress}%` }} />
          </div>
        </div>
      )}
      {stage === 'building' && buildState === 'waiting' && (
        <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-app-border text-caption2 text-app-fg-2">
          <i className="ti ti-clock text-[length:calc(10px*var(--text-scale))] text-app-muted" />Sent · waiting approval
        </div>
      )}

      {/* Ready to invoice */}
      {stage === 'invoice' && <InvoiceFooter trip={trip} selected={invoice?.selected ?? false} onToggle={invoice?.onToggle} />}

      {/* Confirmed footer */}
      {stage === 'confirmed' && (
        <div className="flex items-center gap-1.5 mt-2.5 pt-2.5 border-t border-app-border text-caption2 text-[color:var(--success-fg)]">
          <i className="ti ti-circle-check text-[length:calc(10px*var(--text-scale))]" />Handbook approved
        </div>
      )}

      {/* Active footer */}
      {stage === 'active' && (
        <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-app-border">
          <PulseDot />
          <span className="text-caption2 font-medium text-success">
            {toTripDetail(trip, now).dayOfLabel}
          </span>
        </div>
      )}

      {/* Inquiry submitted */}
      {stage === 'inquiry' && (
        <div className="flex items-center gap-1 mt-2 whitespace-nowrap text-caption2 text-app-muted">
          <i className="ti ti-clock text-[length:calc(10px*var(--text-scale))] shrink-0" />Submitted {relativeTime(trip.created_at)}
        </div>
      )}

      {/* Handbook deadline alert */}
      {(alertUrgent || alertWarn) && (
        <div className={cn(
          'flex items-center gap-1.5 mt-2 px-2.5 py-[7px] rounded-[7px] border text-caption font-medium',
          alertUrgent
            ? 'text-[#E05A6A] bg-[rgba(224,90,106,.08)] border-[rgba(224,90,106,.25)]'
            : 'text-[#D97757] bg-[rgba(217,119,87,.08)] border-[rgba(217,119,87,.25)]',
        )}>
          <i className="ti ti-alert-triangle text-[length:calc(11px*var(--text-scale))] shrink-0" />
          Handbook {trip.progress}% · trip in {until}d
        </div>
      )}

      {/* Inquiry actions */}
      {stage === 'inquiry' && onAccept && onPass && (
        <div className="flex gap-2 mt-2.5">
          <button
            onClick={(e) => { e.stopPropagation(); onAccept(); }}
            className="flex-1 min-h-[36px] md:min-h-0 text-caption font-semibold text-success bg-success/10 border border-success/30 rounded-[7px] py-[7px] transition-colors hover:bg-success/20 sk-hit"
          >
            Accept
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onPass(); }}
            className="flex-1 min-h-[36px] md:min-h-0 text-caption font-medium text-app-muted border border-app-border rounded-[7px] py-[7px] transition-colors hover:bg-app-bg hover:text-app-fg-2 sk-hit"
          >
            Pass
          </button>
        </div>
      )}
    </div>
  );
}

// ── Phone card: full width, one stage at a time ───────────────────────────────

function MobileTripCard({ trip, stage, buildState, now, onOpen, onAccept, onPass, invoice }: CardProps) {
  const until = daysUntil(trip.start_date, now);
  const isDraft = stage === 'building' && buildState === 'draft';
  const urgent = isDraft && until != null && until <= 10;
  const warn = isDraft && until != null && until > 10 && until <= 21;
  const days = trip.start_date && trip.end_date
    ? Math.round((new Date(trip.end_date).getTime() - new Date(trip.start_date).getTime()) / 86400000) + 1
    : null;

  let status: ReactNode = null;
  if (stage === 'inquiry') status = <><i className="ti ti-clock text-[length:calc(12px*var(--text-scale))]" />Submitted {relativeTime(trip.created_at)}</>;
  else if (stage === 'building' && buildState === 'unstarted') status = <><i className="ti ti-file-text text-[length:calc(12px*var(--text-scale))]" />Handbook not started</>;
  else if (stage === 'building' && buildState === 'waiting') status = <><i className="ti ti-clock text-[length:calc(12px*var(--text-scale))]" />Sent · waiting approval</>;
  else if (stage === 'confirmed') status = <span className="inline-flex items-center gap-1.5 text-[color:var(--success-fg)]"><i className="ti ti-circle-check text-[length:calc(12px*var(--text-scale))]" />Handbook approved</span>;
  else if (stage === 'active') status = <span className="inline-flex items-center gap-2 font-medium text-success"><PulseDot />{toTripDetail(trip, now).dayOfLabel}</span>;

  return (
    <div
      onClick={onOpen}
      className={cn(
        'cursor-pointer rounded-[14px] border bg-app-card px-4 py-4 active:bg-app-bg',
        urgent ? 'border-[rgba(224,90,106,.35)] border-l-[5px] border-l-[#E05A6A]'
          : warn ? 'border-[rgba(217,119,87,.35)] border-l-[5px] border-l-[#D97757]'
          : 'border-app-border',
      )}
    >
      <div className="flex items-center gap-2.5">
        <TravelerAvatar trip={trip} />
        <span className="min-w-0 flex-1 truncate text-subhead font-medium text-app-fg">{trip.traveler_name ?? 'Traveler'}</span>
        {days != null && <span className="shrink-0 text-caption text-app-muted">{days} days</span>}
      </div>

      <div className="mt-3 font-serif text-[length:calc(19px*var(--text-scale))] leading-[1.25] text-app-fg [text-wrap:pretty]">{trip.trip_name ?? 'New trip'}</div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2 text-subhead text-app-fg-2">
        {dateRange(trip) && <span className="font-medium">{dateRange(trip)}</span>}
        {trip.destination && <span className="inline-flex items-center gap-1 text-[length:calc(12.5px*var(--text-scale))] text-app-muted"><i className="ti ti-map-pin text-[length:calc(11px*var(--text-scale))]" />{trip.destination}</span>}
      </div>

      {isDraft && (
        <div className="mt-3">
          <div className="mb-1 flex justify-between text-caption">
            <span className={urgent ? 'font-medium text-[#E05A6A]' : warn ? 'font-medium text-[#D97757]' : 'text-app-muted'}>
              {urgent || warn ? `Handbook ${trip.progress}% · trip in ${until}d` : 'Handbook draft'}
            </span>
            {!(urgent || warn) && <span className="text-app-fg-2">{trip.progress}%</span>}
          </div>
          <div className="h-1 overflow-hidden rounded-md bg-app-border">
            <div className="h-full rounded-md bg-violet" style={{ width: `${trip.progress}%` }} />
          </div>
        </div>
      )}
      {status && <div className="mt-3 flex items-center gap-1.5 text-footnote text-app-muted">{status}</div>}
      {stage === 'invoice' && <InvoiceFooter trip={trip} selected={invoice?.selected ?? false} onToggle={invoice?.onToggle} />}

      {stage === 'inquiry' && onAccept && onPass && (
        <div className="mt-3.5 flex gap-2.5">
          <button
            onClick={(e) => { e.stopPropagation(); onAccept(); }}
            className="min-h-[46px] flex-1 rounded-[10px] border border-success/30 bg-success/10 text-subhead font-semibold text-success active:bg-success/20"
          >
            Accept
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onPass(); }}
            className="min-h-[46px] flex-1 rounded-[10px] border border-app-border text-subhead font-medium text-app-muted active:bg-app-bg"
          >
            Pass
          </button>
        </div>
      )}
    </div>
  );
}

// ── Profile nudge — verbatim from the previous Dashboard.tsx ─────────────────

function ProfileNudge({ strength }: { strength: HostProfileStrength }) {
  const blocked = !strength.matchable;
  const remaining = [...strength.ranking.items, ...strength.winning.items].filter((i) => !i.done);
  return (
    <Link
      to="/host/profile"
      className={`sk-hit flex items-center gap-3 rounded-2xl px-3 py-2 mb-3 md:px-4 md:py-3 md:mb-6 transition-colors border ${
        blocked
          ? 'bg-crimson/10 border-crimson/30 hover:bg-crimson/15'
          : 'bg-gold/10 border-gold/30 hover:bg-gold/15'
      }`}
    >
      <div className={`hidden md:flex w-9 h-9 rounded-full items-center justify-center shrink-0 ${blocked ? 'bg-crimson/20' : 'bg-gold/20'}`}>
        <i className={`ti ${blocked ? 'ti-alert-triangle text-crimson' : 'ti-sparkles text-gold'} text-base`} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-app-fg truncate">{strengthHeadline(strength)}</p>
        <p className="text-xs text-app-muted truncate">
          {blocked
            ? strength.blockers.map((b) => b.label).join(' · ')
            : remaining.slice(0, 3).map((r) => r.label).join(' · ')}
        </p>
      </div>
      <span className="text-xs text-app-muted shrink-0">{strength.percent}%</span>
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-4 h-4 shrink-0 ${blocked ? 'text-crimson' : 'text-gold'}`}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
      </svg>
    </Link>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type ViewMode = 'kanban' | 'list' | 'calendar';
type ListFilter = 'all' | TripStage;
type SortCol = 'traveler' | 'dates' | 'status';

const VIEW_KEY = 'sk_trips_view';
const STAGE_ORDER: Record<TripStage, number> = { inquiry: 0, building: 1, confirmed: 2, active: 3, invoice: 4, past: 5 };

export function HostDashboard() {
  const { user, profile, loading } = useAuth();
  const [trips, setTrips] = useState<TripMatch[]>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [strength, setStrength] = useState<HostProfileStrength | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      const saved = localStorage.getItem(VIEW_KEY) as ViewMode | null;
      // The board is the default everywhere; on a phone it shows one stage at a
      // time (see MobileTripBoard). A host's own choice is remembered per device.
      return saved ?? 'kanban';
    } catch { return 'kanban'; }
  });
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [selected, setSelected] = useState<TripDetailData | null>(null);
  const [listFilter, setListFilter] = useState<ListFilter>('all');
  const [sortCol, setSortCol] = useState<SortCol>('dates');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [toast, setToast] = useState('');
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // "Ready to invoice": the trips (match ids) the host has ticked, the invoice form, and a counter to reload after sending.
  const [pickedIds, setPickedIds] = useState<Set<string>>(new Set());
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const now = useMemo(() => new Date(), []);

  function changeView(v: ViewMode) {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ }
  }

  // ── Data loading — same queries as before, now including `proposed` asks ──
  useEffect(() => {
    if (!user) return;
    // Use the shared strength model rather than a local headline+city+bio
    // check, which both named the wrong fields and ignored the two things that
    // actually decide whether a host gets matched: billing details and an
    // active profile.
    void (async () => {
      const [{ data: hp }, { data: billing }, { count }, { data: settings }] = await Promise.all([
        supabase.from('host_profiles').select('*').eq('profile_id', user.id).maybeSingle(),
        supabase.from('host_billing_details').select('host_id').eq('host_id', user.id).maybeSingle(),
        supabase.from('host_destinations').select('destination_id', { count: 'exact', head: true }).eq('host_id', user.id),
        supabase.from('platform_settings').select('require_host_payout_ready').limit(1).maybeSingle(),
      ]);
      setStrength(hostProfileStrength(
        hp as HostProfile | null,
        !!billing,
        count ?? 0,
        profile?.avatar_url,
        (settings as { require_host_payout_ready: boolean } | null)?.require_host_payout_ready !== false,
      ));
    })();
  }, [user, profile?.avatar_url]);

  useEffect(() => {
    if (!user) return;
    async function load() {
      const { data: matchData } = await supabase
        .from('matches')
        .select('id, traveler_id, portrait_id, status, created_at, leg:trip_legs(city, start_date, days), profiles!matches_traveler_id_fkey(full_name, avatar_url)')
        .eq('host_id', user!.id)
        .in('status', ['pending', 'accepted', 'completed'])
        .eq('needs_host', false) // escalated (unassigned) inquiries belong to no host board
        .order('created_at', { ascending: false });

      if (!matchData) { setFetchLoading(false); return; }

      const matchIds = matchData.map((m: { id: string }) => m.id);
      const portraitIds = [...new Set((matchData as { portrait_id: string | null }[]).map((m) => m.portrait_id).filter(Boolean))] as string[];
      const [{ data: itineraryData }, { data: paymentData }, { data: portraitData }] = await Promise.all([
        matchIds.length > 0
          ? supabase.from('itineraries').select('match_id, status, content').in('match_id', matchIds)
          : Promise.resolve({ data: [] }),
        matchIds.length > 0
          ? supabase.from('payments')
              .select('id, match_id, status, payout_status, host_payout_cents, currency')
              .eq('host_id', user!.id)
              .in('match_id', matchIds)
          : Promise.resolve({ data: [] }),
        portraitIds.length > 0
          ? supabase.from('taste_portraits')
              .select('id, host_summary, portrait_json, trip_name, destinations, start_date, trip_days')
              .in('id', portraitIds)
          : Promise.resolve({ data: [] }),
      ]);

      const itinMap = new Map<string, { status: string; content: unknown }>();
      ((itineraryData ?? []) as { match_id: string; status: string; content: unknown }[]).forEach((it) => {
        itinMap.set(it.match_id, { status: it.status, content: it.content });
      });
      type PortraitRow = {
        id: string; host_summary: string | null; portrait_json: Record<string, unknown> | null;
        trip_name: string | null; destinations: string[] | null; start_date: string | null; trip_days: number | null;
      };
      const portraitMap = new Map<string, PortraitRow>();
      ((portraitData ?? []) as PortraitRow[]).forEach((p) => {
        portraitMap.set(p.id, p);
      });

      // The money side: each trip's payment, and which invoice (if any) it is on.
      type PaymentRow = {
        id: string; match_id: string; status: string; payout_status: string;
        host_payout_cents: number; currency: string;
      };
      type InvoiceInfo = { status: string; invoice_number: string; rejected_reason: string | null; submitted_at: string };
      type ItemRow = { payment_id: string; released_at: string | null; host_invoices: InvoiceInfo | null };
      const payRows = (paymentData ?? []) as PaymentRow[];
      const { data: itemData } = payRows.length > 0
        ? await supabase
            .from('host_invoice_items')
            .select('payment_id, released_at, host_invoices(status, invoice_number, rejected_reason, submitted_at)')
            .in('payment_id', payRows.map((p) => p.id))
        : { data: [] };
      const liveInvoice = new Map<string, InvoiceInfo>();
      const lastRejected = new Map<string, InvoiceInfo>();
      ((itemData ?? []) as unknown as ItemRow[]).forEach((it) => {
        const inv = it.host_invoices;
        if (!inv) return;
        if (it.released_at === null) liveInvoice.set(it.payment_id, inv);
        else if (inv.status === 'rejected' && inv.rejected_reason) {
          const prev = lastRejected.get(it.payment_id);
          if (!prev || inv.submitted_at > prev.submitted_at) lastRejected.set(it.payment_id, inv);
        }
      });
      const paymentByMatch = new Map<string, TripPayment>();
      payRows.forEach((p) => {
        const live = liveInvoice.get(p.id) ?? null;
        paymentByMatch.set(p.match_id, {
          id: p.id,
          fee_cents: p.host_payout_cents,
          currency: p.currency,
          state: invoiceState({ status: p.status, payout_status: p.payout_status }, live),
          invoice: live ? { invoice_number: live.invoice_number, status: live.status } : null,
          rejectedReason: lastRejected.get(p.id)?.rejected_reason ?? null,
        });
      });

      const result: TripMatch[] = (matchData as unknown as {
        id: string; traveler_id: string; portrait_id: string | null; status: string; created_at: string;
        leg?: { city: string; start_date: string; days: number } | { city: string; start_date: string; days: number }[] | null;
        profiles: { full_name: string | null } | null;
      }[]).map((m) => {
        // A trip with several cities: this host's board shows their own city and days.
        const leg = Array.isArray(m.leg) ? (m.leg[0] ?? null) : (m.leg ?? null);
        const itin = itinMap.get(m.id);
        const hb = itin && isHandbook(itin.content) ? itin.content : null;
        const { progress, events } = handbookProgress(hb);
        const portrait = m.portrait_id ? portraitMap.get(m.portrait_id) ?? null : null;
        const pj = portrait?.portrait_json ?? null;
        // A fresh inquiry has no itinerary/handbook yet — the host built it
        // AFTER accepting — so trip name/city/dates fell back to nothing at
        // all instead of falling back to the taste_portrait that already has
        // them (the same fields the traveler's own trip summary uses).
        const portraitDestination = portrait?.destinations?.length ? formatPlaces(portrait.destinations) : null;
        const portraitEndDate = portrait?.start_date && portrait?.trip_days
          ? addDaysISO(portrait.start_date, portrait.trip_days - 1)
          : null;
        return {
          id: m.id,
          traveler_id: m.traveler_id,
          status: m.status,
          created_at: m.created_at,
          traveler_name: m.profiles?.full_name ?? null,
          itinerary_status: itin?.status ?? null,
          trip_name: hb?.trip_name ?? portrait?.trip_name ?? null,
          destination: hb?.destination ?? leg?.city ?? portraitDestination,
          start_date: hb?.start_date ?? leg?.start_date ?? portrait?.start_date ?? null,
          end_date: hb?.end_date ?? (leg ? addDaysISO(leg.start_date, leg.days - 1) : portraitEndDate),
          events_planned: events,
          progress,
          alma_summary: portrait?.host_summary ?? null,
          interests: extractInterests(pj),
          ask: extractAsk(pj),
          pace: extractPace(pj),
          party: extractParty(pj),
          dealbreakers: extractDealbreakers(pj),
          payment: paymentByMatch.get(m.id) ?? null,
        };
      });

      setTrips(result);
      setFetchLoading(false);
    }
    void load();
  }, [user, reloadKey]);

  // ── New mutations: accept / pass an inquiry ──
  function showToast(message: string) {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 2400);
  }

  async function acceptInquiry(matchId: string) {
    const trip = trips.find((t) => t.id === matchId);
    // Moves the match to 'accepted' and writes "Alma left / host joined" into the
    // linked conversation, flipping the traveler's chat live. Idempotent.
    const { error } = await supabase.rpc('host_confirm_trip', { p_match_id: matchId });
    if (!error) {
      const name = trip?.traveler_name?.split(' ')[0];
      setTrips((prev) => prev.map((t) => (t.id === matchId ? { ...t, status: 'accepted' } : t)));
      setSelected(null);
      showToast(name ? `Trip confirmed — ${name} moved to Building` : 'Trip confirmed');
    }
  }
  async function passInquiry(matchId: string) {
    // Re-routes to the traveler's other proposed host (same match row), or flags
    // for admin if there is none. Returns the new host_id, or null when escalated.
    const { data: newHostId, error } = await supabase.rpc('host_pass_trip', { p_match_id: matchId });
    if (error) { showToast('Could not pass this inquiry'); return; }
    setTrips((prev) => prev.filter((t) => t.id !== matchId));
    setSelected(null);
    if (newHostId) {
      showToast('Passed — offered to their other host');
    } else {
      showToast('Passed — sent to Sekretly for rematching');
    }
  }

  // ── Derived buckets ──
  const staged = useMemo(() => trips.map((t) => ({ trip: t, ...deriveStage(t, now) })), [trips, now]);
  const byStage = (s: TripStage) => staged.filter((x) => x.stage === s);
  const recent = byStage('past').filter((x) => !x.trip.end_date || daysUntil(x.trip.end_date, now)! >= -30);
  const pipelineCount = staged.filter((x) => ['building', 'confirmed', 'active'].includes(x.stage)).length;
  const inquiryCount = byStage('inquiry').length;
  // Ready to invoice: what the host can tick right now, and what is ticked.
  const dueTrips = staged.filter((x) => x.stage === 'invoice' && x.trip.payment?.state === 'due');
  const pickedTrips = dueTrips.filter((x) => pickedIds.has(x.trip.id));
  const pickedTotal = pickedTrips.reduce((n, x) => n + x.trip.payment!.fee_cents, 0);
  const pickedCurrency = pickedTrips[0]?.trip.payment?.currency ?? 'eur';
  const dueTotal = dueTrips.reduce((n, x) => n + x.trip.payment!.fee_cents, 0);

  function togglePick(t: TripMatch) {
    if (pickedIds.has(t.id)) {
      setPickedIds((prev) => { const next = new Set(prev); next.delete(t.id); return next; });
      return;
    }
    if (pickedTrips.length > 0 && pickedTrips[0].trip.payment!.currency !== t.payment!.currency) {
      showToast('One invoice can only cover trips in the same currency');
      return;
    }
    setPickedIds((prev) => new Set(prev).add(t.id));
  }
  function pickAllDue() {
    const everyone = pickedTrips.length === dueTrips.length;
    const first = dueTrips[0]?.trip.payment?.currency;
    setPickedIds(everyone ? new Set() : new Set(dueTrips.filter((x) => x.trip.payment!.currency === first).map((x) => x.trip.id)));
  }
  const nextTrip = staged
    .filter((x) => x.trip.start_date && new Date(x.trip.start_date) > now && x.stage !== 'inquiry' && x.stage !== 'past')
    .sort((a, b) => a.trip.start_date!.localeCompare(b.trip.start_date!))[0];

  const open = (t: TripMatch) => setSelected(toTripDetail(t, now));
  if (loading || fetchLoading) {
    return (
      <div className="min-h-dvh bg-app-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const KANBAN_COLS: TripStage[] = ['inquiry', 'building', 'confirmed', 'active', 'invoice'];
  const invoiceProps = (t: TripMatch) => ({
    selected: pickedIds.has(t.id),
    onToggle: t.payment?.state === 'due' ? () => togglePick(t) : undefined,
  });

  // ── List rows ──
  const listRows = staged
    .filter((x) => listFilter === 'all' || x.stage === listFilter)
    .sort((a, b) => {
      let va: string | number, vb: string | number;
      if (sortCol === 'traveler') { va = (a.trip.traveler_name ?? '').toLowerCase(); vb = (b.trip.traveler_name ?? '').toLowerCase(); }
      else if (sortCol === 'status') { va = STAGE_ORDER[a.stage]; vb = STAGE_ORDER[b.stage]; }
      else { va = a.trip.start_date ?? '9999'; vb = b.trip.start_date ?? '9999'; }
      const cmp = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  function toggleSort(col: SortCol) {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  }

  const SortHeader = ({ col, children }: { col: SortCol; children: ReactNode }) => (
    <button onClick={() => toggleSort(col)} className={cn('sk-hit', 'inline-flex items-center gap-1 text-caption font-medium text-left', sortCol === col ? 'text-app-fg' : 'text-app-muted')}>
      {children}
      <i className={cn('ti text-caption2 opacity-70', sortCol === col ? (sortDir === 'asc' ? 'ti-chevron-up' : 'ti-chevron-down') : 'ti-selector')} />
    </button>
  );

  // ── Calendar weeks (spanning timeline with lane packing) ──
  const DAY = 86400000;
  const monthStart = calMonth;
  const gridStart = new Date(monthStart.getTime() - ((monthStart.getDay() + 6) % 7) * DAY); // back to Monday
  const events = staged
    .filter((x) => x.trip.start_date && x.trip.end_date)
    .map((x) => ({
      ...x,
      startD: new Date(x.trip.start_date! + 'T00:00:00'),
      endD: new Date(x.trip.end_date! + 'T00:00:00'),
    }))
    .sort((a, b) => a.startD.getTime() - b.startD.getTime());
  const dayIndex = (d: Date) => Math.round((d.getTime() - gridStart.getTime()) / DAY);

  const weeks = Array.from({ length: 5 }, (_, w) => {
    const weekStartIdx = w * 7;
    const days = Array.from({ length: 7 }, (_, d) => {
      const cell = new Date(gridStart.getTime() + (weekStartIdx + d) * DAY);
      return {
        num: cell.getDate(),
        isToday: cell.toDateString() === now.toDateString(),
        inMonth: cell.getMonth() === monthStart.getMonth(),
        monthTag: cell.getDate() === 1 ? cell.toLocaleString('en', { month: 'short' }) : null,
        weekend: d >= 5,
      };
    });
    // clip events into segments, greedy lane packing
    const segs = events.flatMap((e) => {
      const s = Math.max(dayIndex(e.startD), weekStartIdx);
      const en = Math.min(dayIndex(e.endD), weekStartIdx + 6);
      if (s > en) return [];
      return [{
        e,
        colStart: s - weekStartIdx + 1,
        span: en - s + 1,
        trueStart: dayIndex(e.startD) >= weekStartIdx,
        lane: 0,
      }];
    });
    const laneEnds: number[] = [];
    segs.forEach((sg) => {
      let lane = laneEnds.findIndex((end) => sg.colStart > end);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(0); }
      laneEnds[lane] = sg.colStart + sg.span - 1;
      sg.lane = lane;
    });
    return { days, segs, laneCount: laneEnds.length };
  });

  const mobileStages: BoardStage[] = KANBAN_COLS.map((id) => {
    const cards = byStage(id);
    return {
      id,
      label: id === 'active' ? 'Active' : id === 'inquiry' ? 'Inquiries' : id === 'invoice' ? 'To invoice' : STAGE_META[id].label,
      icon: STAGE_META[id].icon,
      text: STAGE_META[id].text,
      chip: STAGE_META[id].chip,
      count: cards.length,
      empty: id === 'inquiry' ? 'No new inquiries.' : id === 'active' ? 'No trip is running right now.' : id === 'invoice' ? 'No finished trips to invoice.' : 'Nothing here right now.',
      cards: <>
        {id === 'invoice' && dueTrips.length > 1 && (
          <button type="button" onClick={pickAllDue} className="mb-2 min-h-[44px] w-full rounded-[10px] border border-app-border text-footnote font-medium text-gold">
            {pickedTrips.length === dueTrips.length ? 'Clear selection' : `Select all ${dueTrips.length} trips`}
          </button>
        )}
        {cards.map(({ trip, stage, buildState }) => (
        <MobileTripCard
          key={trip.id}
          trip={trip}
          stage={stage}
          buildState={buildState}
          now={now}
          onOpen={() => open(trip)}
          onAccept={stage === 'inquiry' ? () => void acceptInquiry(trip.id) : undefined}
          onPass={stage === 'inquiry' ? () => void passInquiry(trip.id) : undefined}
          invoice={stage === 'invoice' ? invoiceProps(trip) : undefined}
        />
      ))}
      </>,
    };
  });

  const mobileRecent = recent.length > 0 ? (
    <div className="mt-2 border-t border-app-border pt-3">
      <button
        type="button"
        onClick={() => setArchiveOpen((o) => !o)}
        aria-expanded={archiveOpen}
        className="flex min-h-[44px] w-full items-center gap-2 text-left text-footnote font-medium text-app-muted"
      >
        <i className="ti ti-clock-check text-[length:calc(15px*var(--text-scale))]" aria-hidden="true" />
        Completed · last 30 days
        <span className="rounded-pill border border-app-border bg-app-card px-2 text-caption">{recent.length}</span>
        <i className={cn('ti ml-auto text-subhead', archiveOpen ? 'ti-chevron-up' : 'ti-chevron-down')} aria-hidden="true" />
      </button>
      {archiveOpen && (
        <div className="mt-2 flex flex-col gap-2 pb-4 opacity-70">
          {recent.map(({ trip }) => (
            <div key={trip.id} onClick={() => open(trip)} className="cursor-pointer rounded-[12px] border border-app-border bg-app-card px-4 py-3">
              <div className="flex items-center gap-2.5">
                <TravelerAvatar trip={trip} size="sm" />
                <span className="min-w-0 flex-1 truncate text-footnote font-medium text-app-fg">{trip.traveler_name ?? 'Traveler'}</span>
                {trip.end_date && <span className="text-caption text-app-muted">{completedLabel(trip.end_date, now).replace('Completed ', '')}</span>}
              </div>
              <div className="mt-1.5 font-serif text-subhead text-app-fg">{trip.trip_name ?? 'Trip'}</div>
              <div className="text-caption text-app-muted">{dateRange(trip)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : null;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-5 md:px-10 py-5 md:py-10 flex flex-col min-h-dvh">

        {/* ── Header ── */}
        <div className="shrink-0 mb-3 md:mb-5">
          <div className="text-caption font-medium text-app-muted mb-1 md:mb-2">
            {now.toLocaleDateString('en', { month: 'short', year: 'numeric' })} · your season
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
            <div className="min-w-0">
              <h1 className="font-serif text-[length:calc(28px*var(--text-scale))] md:text-[length:calc(34px*var(--text-scale))] font-normal leading-[1.06] tracking-[-.015em] text-app-fg">Your trips</h1>
              <div className="flex items-center gap-x-3 gap-y-1 md:gap-[18px] mt-2 md:mt-3 flex-wrap">
                <span className="flex items-baseline gap-1.5">
                  <span className="font-serif text-[length:calc(19px*var(--text-scale))] text-app-fg leading-none">{pipelineCount}</span>
                  <span className="text-caption text-app-fg-2">in pipeline</span>
                </span>
                <span className="w-px h-[18px] bg-app-border" />
                <span className="flex items-baseline gap-1.5">
                  <span className="font-serif text-[length:calc(19px*var(--text-scale))] text-app-fg leading-none">{inquiryCount}</span>
                  <span className="text-caption text-app-fg-2">new {inquiryCount === 1 ? 'inquiry' : 'inquiries'}</span>
                </span>
                {dueTrips.length > 0 && (
                  <>
                    <span className="w-px h-[18px] bg-app-border" />
                    <span className="flex items-baseline gap-1.5">
                      <span className="font-serif text-[length:calc(19px*var(--text-scale))] text-gold leading-none">{formatMoney(dueTotal, dueTrips[0].trip.payment!.currency)}</span>
                      <span className="text-caption text-app-fg-2">to invoice</span>
                    </span>
                  </>
                )}
                {nextTrip && (
                  <>
                    <span className="w-px h-[18px] bg-app-border" />
                    <span className="inline-flex items-center gap-1.5 text-caption text-app-muted">
                      <i className="ti ti-arrow-up-right text-[length:calc(13px*var(--text-scale))]" />
                      Next trip
                      <span className="text-app-fg font-medium">
                        {new Date(nextTrip.trip.start_date!).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                      </span>
                    </span>
                  </>
                )}
              </div>
            </div>
            {/* View switcher */}
            <div className="flex gap-1.5 shrink-0">
              {(['kanban', 'list', 'calendar'] as ViewMode[]).map((v) => (
                <button
                  key={v}
                  onClick={() => changeView(v)}
                  className={cn('sk-hit', 
                    'min-h-[38px] md:min-h-0 rounded-pill px-3.5 md:px-[15px] py-[7px] text-footnote font-medium transition-colors capitalize',
                    view === v ? 'bg-app-fg text-app-bg' : 'bg-app-card text-app-fg-2 hover:text-app-fg',
                  )}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {strength && strength.percent < 100 && <ProfileNudge strength={strength} />}

        {fetchLoading ? (
          <div className="flex justify-center py-20"><Spinner /></div>
        ) : (
          <>
            {/* ── Kanban ── */}
            {view === 'kanban' && (
              <>
              <div className="md:hidden">
                <MobileTripBoard
                  initial={defaultStage(Object.fromEntries(KANBAN_COLS.map((id) => [id, id === 'invoice' ? dueTrips.length : byStage(id).length])), KANBAN_COLS)}
                  stages={mobileStages}
                  footer={mobileRecent}
                />
              </div>
              <div className="hidden md:block flex-1 overflow-x-auto pb-8 snap-x snap-proximity overscroll-x-contain">
                <div className="flex gap-3 items-start w-max pr-8 h-full">
                  {KANBAN_COLS.map((stageId) => {
                    const m = STAGE_META[stageId];
                    const cards = byStage(stageId);
                    return (
                      <div key={stageId} className="w-[230px] md:w-60 shrink-0 flex flex-col snap-start">
                        <div className="flex items-center gap-[7px] mb-1 px-0.5">
                          {stageId === 'active' && <PulseDot />}
                          <i className={cn('ti text-footnote', m.icon, m.text)} />
                          <span className={cn('text-caption font-medium', m.text)}>{m.label}</span>
                          <span className={cn('rounded-pill border px-2 py-px text-caption font-medium', m.chip)}>{cards.length}</span>
                          {stageId === 'invoice' && dueTrips.length > 1 && (
                            <button type="button" onClick={pickAllDue} className="ml-auto text-caption text-gold underline underline-offset-2 sk-tall">
                              {pickedTrips.length === dueTrips.length ? 'Clear' : 'Select all'}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-col gap-[9px] pt-1.5 pr-1 pb-2.5">
                          {cards.length === 0 && (
                            <p className="text-app-muted text-caption text-center py-6 px-3">
                              {stageId === 'inquiry' ? 'No new inquiries.' : stageId === 'invoice' ? 'No finished trips to invoice.' : 'Nothing here right now.'}
                            </p>
                          )}
                          {cards.map(({ trip, stage, buildState }) => (
                            <KanbanCard
                              key={trip.id}
                              trip={trip}
                              stage={stage}
                              buildState={buildState}
                              now={now}
                              onOpen={() => open(trip)}
                              onAccept={stage === 'inquiry' ? () => void acceptInquiry(trip.id) : undefined}
                              onPass={stage === 'inquiry' ? () => void passInquiry(trip.id) : undefined}
                              invoice={stage === 'invoice' ? invoiceProps(trip) : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}

                  {/* Recent — collapsible rail */}
                  {!archiveOpen ? (
                    <div className="w-[46px] shrink-0 flex flex-col self-stretch">
                      <div className="h-[17px] mb-1" />
                      <button
                        onClick={() => setArchiveOpen(true)}
                        title="Recent"
                        className="flex-1 min-h-[180px] w-full bg-app-card border border-app-border rounded-[10px] flex flex-col items-center py-3.5 gap-2.5 transition-colors hover:border-app-muted"
                      >
                        <i className="ti ti-chevron-left text-[length:calc(14px*var(--text-scale))] text-app-muted" />
                        <i className="ti ti-clock-check text-[length:calc(15px*var(--text-scale))] text-app-muted" />
                        <span className="font-serif text-[length:calc(20px*var(--text-scale))] text-app-fg leading-none">{recent.length}</span>
                        <span className="[writing-mode:vertical-rl] text-caption2 text-app-muted mt-0.5">Recent</span>
                      </button>
                    </div>
                  ) : (
                    <div className="w-60 shrink-0 flex flex-col">
                      <div className="flex items-center gap-[7px] mb-1 px-0.5">
                        <i className="ti ti-clock-check text-[length:calc(13px*var(--text-scale))] text-app-muted" />
                        <span className="text-caption font-medium text-app-muted">Recent</span>
                        <span className="text-caption text-app-muted bg-app-card border border-app-border rounded-pill px-[7px] py-px ml-0.5">last 30d</span>
                        <button onClick={() => setArchiveOpen(false)} title="Collapse" className="ml-auto w-[22px] h-[22px] flex items-center justify-center rounded-[5px] text-app-muted transition-colors hover:bg-app-card hover:text-app-fg sk-tall">
                          <i className="ti ti-chevron-right text-[length:calc(14px*var(--text-scale))]" />
                        </button>
                      </div>
                      <div className="flex flex-col gap-[9px] pt-1.5 pr-1 pb-2.5 opacity-60">
                        {recent.map(({ trip }) => (
                          <div key={trip.id} onClick={() => open(trip)} className="bg-app-card border border-app-border rounded-[10px] px-3.5 py-3 cursor-pointer">
                            <div className="flex items-center gap-2 mb-[7px]">
                              <TravelerAvatar trip={trip} size="sm" />
                              <span className="text-caption font-medium text-app-fg flex-1 min-w-0 truncate">{trip.traveler_name ?? 'Traveler'}</span>
                            </div>
                            <div className="font-serif text-footnote text-app-fg leading-[1.3] mb-1.5">{trip.trip_name ?? 'Trip'}</div>
                            <div className="flex items-center justify-between text-caption2 text-app-muted">
                              <span>{dateRange(trip)}</span>
                              {trip.end_date && <span className="text-caption2">{completedLabel(trip.end_date, now).replace('Completed ', '')}</span>}
                            </div>
                          </div>
                        ))}
                        {recent.length === 0 && <p className="text-app-muted text-caption text-center py-6">No recent trips.</p>}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </>
            )}

            {/* ── List ── */}
            {view === 'list' && (
              <div className="flex-1 pb-10 flex flex-col">
                <div className="flex items-center gap-1.5 mb-[18px] md:flex-wrap overflow-x-auto md:overflow-visible -mx-5 px-5 md:mx-0 md:px-0 shrink-0">
                  {(['all', 'inquiry', 'building', 'confirmed', 'active', 'invoice', 'past'] as ListFilter[]).map((f) => (
                    <button
                      key={f}
                      onClick={() => setListFilter(f)}
                      className={cn(
                        'min-h-[44px] md:min-h-0 shrink-0 whitespace-nowrap rounded-pill px-[15px] py-[7px] text-footnote font-medium transition-colors',
                        listFilter === f ? 'bg-app-fg text-app-bg' : 'bg-app-card text-app-fg-2 hover:text-app-fg',
                      )}
                    >
                      {f === 'all' ? 'All' : f === 'inquiry' ? 'Inquiries' : f === 'past' ? 'Recent' : STAGE_META[f].label}
                    </button>
                  ))}
                  <span className="ml-auto pl-2 text-caption text-app-muted whitespace-nowrap">{listRows.length} trips</span>
                </div>

                {/* Desktop table */}
                <div className="hidden md:block">
                  <div className="grid grid-cols-[2fr_2.2fr_1.4fr_1.3fr_1.2fr] border-b border-app-border pb-[9px] mb-0.5">
                    <SortHeader col="traveler">Traveler</SortHeader>
                    <span className="text-caption font-medium text-app-muted">Trip</span>
                    <SortHeader col="dates">Dates</SortHeader>
                    <SortHeader col="status">Status</SortHeader>
                    <span className="text-caption font-medium text-app-muted">Handbook</span>
                  </div>
                  {listRows.map(({ trip, stage, buildState }) => (
                    <div
                      key={trip.id}
                      onClick={() => open(trip)}
                      className="grid grid-cols-[2fr_2.2fr_1.4fr_1.3fr_1.2fr] items-center px-0.5 py-[11px] border-b border-app-border cursor-pointer transition-colors hover:bg-app-card"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <TravelerAvatar trip={trip} size="sm" />
                        <span className="text-footnote font-medium text-app-fg truncate">{trip.traveler_name ?? 'Traveler'}</span>
                      </div>
                      <div className="font-serif text-subhead text-app-fg pr-4 truncate">{trip.trip_name ?? '—'}</div>
                      <div className="text-caption text-app-fg-2">{dateRange(trip) ?? '—'}</div>
                      <div><StageChip stage={stage} label={stage === 'building' && buildState ? BUILD_LABEL[buildState] : undefined} /></div>
                      <div className={cn('text-caption', stage === 'confirmed' || stage === 'active' ? 'text-[color:var(--success-fg)]' : 'text-app-muted')}>
                        {stage === 'building' ? (buildState ? BUILD_LABEL[buildState] : 'Building') : stage === 'confirmed' || stage === 'active' ? 'Approved' : '—'}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile rows */}
                <div className="flex flex-col gap-2 md:hidden">
                  {listRows.map(({ trip, stage, buildState }) => (
                    <div key={trip.id} onClick={() => open(trip)} className="bg-app-card border border-app-border rounded-[10px] px-3.5 py-3 cursor-pointer">
                      <div className="flex items-center gap-2 mb-1.5">
                        <TravelerAvatar trip={trip} size="sm" />
                        <span className="text-footnote font-medium text-app-fg flex-1 min-w-0 truncate">{trip.traveler_name ?? 'Traveler'}</span>
                        <StageChip stage={stage} label={stage === 'building' && buildState ? BUILD_LABEL[buildState] : undefined} />
                      </div>
                      <div className="font-serif text-subhead text-app-fg truncate">{trip.trip_name ?? '—'}</div>
                      {dateRange(trip) && <div className="text-caption text-app-muted mt-1">{dateRange(trip)}</div>}
                      {stage === 'building' && (
                        <div className="mt-2.5 flex items-center gap-2">
                          <div className="flex-1 h-[3px] bg-app-border rounded-md overflow-hidden">
                            <div className="h-full rounded-md" style={{ width: `${trip.progress}%`, background: 'rgb(var(--violet))' }} />
                          </div>
                          <span className="text-caption2 text-app-muted">{trip.progress}% built</span>
                        </div>
                      )}
                      {stage === 'inquiry' && (
                        <>
                          {trip.ask && <p className="text-caption text-app-fg-2 leading-snug mt-2 line-clamp-3">{trip.ask}</p>}
                          <div className="flex gap-2 mt-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); void acceptInquiry(trip.id); }}
                              className="flex-1 min-h-[44px] text-footnote font-semibold text-success bg-success/10 border border-success/30 rounded-[9px]"
                            >
                              Accept
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); void passInquiry(trip.id); }}
                              className="flex-1 min-h-[44px] text-footnote font-medium text-app-muted border border-app-border rounded-[9px]"
                            >
                              Pass
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {listRows.length === 0 && (
                  <div className="flex-1 flex flex-col items-center justify-center gap-2 py-12 text-app-muted">
                    <i className="ti ti-filter-off text-[length:calc(24px*var(--text-scale))]" />
                    <span className="text-footnote">No trips match this filter</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Calendar ── */}
            {view === 'calendar' && (
              <div className="flex-1 pb-10">
                <div>
                  <div className="flex items-center gap-4 mb-5">
                    <button
                      onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                      className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 border border-app-border rounded-[7px] px-2.5 py-1.5 text-app-muted transition-colors hover:border-app-muted"
                    >
                      <i className="ti ti-chevron-left text-[length:calc(12px*var(--text-scale))]" />
                    </button>
                    <span className="font-serif text-[length:calc(20px*var(--text-scale))] text-app-fg">
                      {calMonth.toLocaleDateString('en', { month: 'long', year: 'numeric' })}
                    </span>
                    <button
                      onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                      className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 border border-app-border rounded-[7px] px-2.5 py-1.5 text-app-muted transition-colors hover:border-app-muted"
                    >
                      <i className="ti ti-chevron-right text-[length:calc(12px*var(--text-scale))]" />
                    </button>
                    <div className="hidden md:flex items-center gap-3.5 ml-auto">
                      {(['inquiry', 'building', 'confirmed', 'active', 'invoice'] as TripStage[]).map((s) => (
                        <div key={s} className="flex items-center gap-1.5 text-caption text-app-muted">
                          <span className={cn('w-2 h-2 rounded-[2px] shrink-0', STAGE_META[s].dot)} />
                          {s === 'inquiry' ? 'Inquiry' : s === 'active' ? 'Active' : STAGE_META[s].label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Mobile agenda — the week-lane grid needs ≥640px, so phones get a list */}
                  <div className="md:hidden flex flex-col gap-2">
                    {(() => {
                      const monthEnd = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 0);
                      const monthEvents = events.filter((e) => e.startD <= monthEnd && e.endD >= monthStart);
                      if (monthEvents.length === 0) {
                        return (
                          <div className="flex flex-col items-center gap-2 py-12 text-app-muted">
                            <i className="ti ti-calendar-off text-[length:calc(24px*var(--text-scale))]" />
                            <span className="text-footnote">No trips this month</span>
                          </div>
                        );
                      }
                      return monthEvents.map((e) => {
                        const m = STAGE_META[e.stage];
                        return (
                          <div
                            key={e.trip.id}
                            onClick={() => open(e.trip)}
                            className={cn('bg-app-card border border-app-border rounded-[10px] px-3.5 py-3 cursor-pointer border-l-[3px]', e.stage === 'past' && 'opacity-70')}
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className={cn('w-2 h-2 rounded-[2px] shrink-0', m.dot)} />
                              <span className="text-footnote font-medium text-app-fg flex-1 min-w-0 truncate">{e.trip.traveler_name ?? 'Traveler'}</span>
                              <StageChip stage={e.stage} />
                            </div>
                            <div className="font-serif text-subhead text-app-fg truncate">{e.trip.trip_name ?? '—'}</div>
                            <div className="text-caption text-app-muted mt-1">{dateRange(e.trip)}</div>
                          </div>
                        );
                      });
                    })()}
                  </div>

                  {/* Desktop week-lane grid */}
                  <div className="hidden md:block overflow-x-auto">
                  <div className="min-w-[640px]">
                  <div className="grid grid-cols-7 mb-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, i) => (
                      <div key={d} className={cn('text-caption font-medium text-app-muted px-2', i >= 5 && 'opacity-70')}>{d}</div>
                    ))}
                  </div>

                  {weeks.map((week, wi) => (
                    <div key={wi} className="border border-app-border rounded-[10px] overflow-hidden bg-app-card mb-2">
                      <div className="grid grid-cols-7">
                        {week.days.map((day, di) => (
                          <div key={di} className={cn('px-2 py-1.5 min-h-[32px] flex items-center gap-1.5 border-r border-b border-app-border last:border-r-0', day.weekend && 'bg-app-bg/60')}>
                            <span className={cn(
                              'inline-flex items-center justify-center w-[21px] h-[21px] rounded-full text-caption shrink-0',
                              day.isToday ? 'bg-gold text-ink font-bold' : day.inMonth ? 'text-app-fg-2 font-medium' : 'text-app-muted opacity-45 font-medium',
                            )}>{day.num}</span>
                            {day.monthTag && <span className="text-caption2 font-medium text-app-muted">{day.monthTag}</span>}
                          </div>
                        ))}
                      </div>
                      <div className="relative py-1.5" style={{ minHeight: Math.max(week.laneCount, 1) * 30 + 6 }}>
                        <div className="absolute inset-0 grid grid-cols-7 pointer-events-none">
                          {Array.from({ length: 7 }, (_, i) => <div key={i} className={cn(i < 6 && 'border-r border-app-border')} />)}
                        </div>
                        {Array.from({ length: week.laneCount }, (_, l) => (
                          <div key={l} className="grid grid-cols-7 mb-1 relative">
                            {week.segs.filter((sg) => sg.lane === l).map((sg) => {
                              const m = STAGE_META[sg.e.stage];
                              return (
                                <div
                                  key={sg.e.trip.id}
                                  onClick={() => open(sg.e.trip)}
                                  title={`${sg.e.trip.traveler_name} · ${sg.e.trip.trip_name}`}
                                  className={cn('sk-tall', 'h-[26px] mx-0.5 flex items-center gap-1.5 px-2 rounded-[7px] border cursor-pointer overflow-hidden min-w-0', m.bar, sg.e.stage === 'past' && 'opacity-70')}
                                  style={{ gridColumn: `${sg.colStart} / span ${sg.span}`, gridRow: 1, borderLeftWidth: sg.trueStart ? 3 : 1 }}
                                >
                                  <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', m.dot)} />
                                  <span className="flex-1 min-w-0 flex items-baseline gap-1.5 overflow-hidden">
                                    <span className="text-caption font-semibold text-app-fg whitespace-nowrap">{sg.e.trip.traveler_name?.split(' ')[0]}</span>
                                    <span className="text-caption2 text-app-fg-2 truncate flex-1 min-w-0">{sg.e.trip.trip_name}</span>
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Trip detail slide-over */}
      <TripDetailPanel
        trip={selected}
        onClose={() => setSelected(null)}
        onAccept={(id) => void acceptInquiry(id)}
        onPass={(id) => void passInquiry(id)}
        previewHref={selected ? `/host/preview/${selected.matchId}` : undefined}
      />

      {pickedTrips.length > 0 && !invoiceOpen && !selected && (
        <div
          role="region"
          aria-label="Invoice selection"
          className="fixed left-1/2 z-40 flex -translate-x-1/2 items-center gap-3 rounded-pill bg-app-fg py-2 pl-4 pr-2 text-app-bg shadow-lg bottom-[calc(96px+env(safe-area-inset-bottom,0px))] md:bottom-6"
        >
          <span className="whitespace-nowrap text-footnote font-medium">
            {pickedTrips.length} trip{pickedTrips.length === 1 ? '' : 's'} · {formatMoney(pickedTotal, pickedCurrency)}
          </span>
          <button
            type="button"
            onClick={() => setInvoiceOpen(true)}
            className="min-h-[36px] rounded-pill bg-gold px-3.5 text-footnote font-semibold text-ink sk-hit"
          >
            Create invoice
          </button>
          <button
            type="button"
            aria-label="Clear selection"
            onClick={() => setPickedIds(new Set())}
            className="flex h-9 w-9 items-center justify-center rounded-full text-app-bg/70 sk-hit"
          >
            <i className="ti ti-x text-[length:calc(14px*var(--text-scale))]" aria-hidden="true" />
          </button>
        </div>
      )}

      {invoiceOpen && user && pickedTrips.length > 0 && (
        <HostInvoiceModal
          hostId={user.id}
          currency={pickedCurrency}
          trips={pickedTrips.map((x) => ({
            paymentId: x.trip.payment!.id,
            matchId: x.trip.id,
            title: x.trip.trip_name ?? 'Trip',
            dates: dateRange(x.trip) ?? '',
            feeCents: x.trip.payment!.fee_cents,
          }))}
          onClose={() => setInvoiceOpen(false)}
          onSubmitted={() => {
            setInvoiceOpen(false);
            setPickedIds(new Set());
            setReloadKey((k) => k + 1);
            showToast('Invoice sent. We’ll review it and pay it in the next payment run.');
          }}
        />
      )}

      <Toast message={toast} />
    </div>
  );
}

export default HostDashboard;
